import { Switch } from "antd";
import { Table, Tabs } from "src/components";
import { PaymentSettingColumns } from "src/components/TableColumns/PaymentSettingColumn";
import DeskPanel from "src/components/DeskPanel/DesktPanel";
import { UpdatePaymentSettingRequest, useListPaymentSetting, useUpdatePaymentSetting } from "src/hooks/usePaymentSetting";
import { useMemo, useState } from "react";
import { TableProps } from "antd/es/table";
import { PaymentGateway, PaymentSetting, PaymentSettingType } from "src/types";
import { FilterValue } from "antd/lib/table/interface";
import "./PaymentSetting.scss";

export enum PaymentSettingTab {
  QR = "QR",
  PARTNER = "PARTNER",
}

// Gateways integrated through a partner. Everything else belongs to the QR tab,
// so add a gateway here only when it ships with the partner integration.
const PARTNER_GATEWAYS: PaymentSettingType[] = [PaymentGateway.SECUREPAY, PaymentGateway.P2PAY];

interface IPaymentPageProps {}

export const PaymentSettingPage: React.FunctionComponent<IPaymentPageProps> = () => {
  const { data: paymentList, isLoading } = useListPaymentSetting();
  const { mutate: updatePaymentSetting, isLoading: isUpdating } = useUpdatePaymentSetting();
  const [selectedTab, setSelectedTab] = useState<PaymentSettingTab>(PaymentSettingTab.QR);
  const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue | null>>({});

  const isPartnerTab = selectedTab === PaymentSettingTab.PARTNER;

  const tabPaymentList = useMemo(
    () =>
      paymentList?.filter((payment) => {
        const isPartner = PARTNER_GATEWAYS.includes(payment.type);
        return selectedTab === PaymentSettingTab.PARTNER ? isPartner : !isPartner;
      }),
    [paymentList, selectedTab]
  );

  // The bank channel is one switch shared by every partner gateway; the backend
  // copies is_bank_enabled across all of them, so one request is enough. Read it
  // as "every partner is on" rather than "any": when the rows disagree the
  // switch shows off, which is what keeps "turn it on" clickable and lets one
  // click bring them back in step.
  const isBankEnabled = useMemo(
    () =>
      !!tabPaymentList?.length &&
      tabPaymentList.every((payment) => payment.isBankEnabled === true),
    [tabPaymentList]
  );

  const bankToggleTarget = tabPaymentList?.[0];

  const handleChange: TableProps<PaymentSetting>["onChange"] = (_, filters) => {
    setFilteredInfo(filters);
  };

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab as PaymentSettingTab);
    // Filter options are derived from the visible list, so they don't carry over.
    setFilteredInfo({});
  };

  const handleUpdatePaymentSetting = (params: UpdatePaymentSettingRequest) => {
    updatePaymentSetting(params);
  };

  const handleBankEnabledChange = (checked: boolean) => {
    if (!bankToggleTarget) return;
    handleUpdatePaymentSetting({ ...bankToggleTarget, isBankEnabled: checked });
  };

  return (
    <div className="game-page">
      <Tabs activeKey={selectedTab} onChange={handleTabChange}>
        <Tabs.TabPane tab="QR" key={PaymentSettingTab.QR} />
        <Tabs.TabPane tab="Partner" key={PaymentSettingTab.PARTNER} />
      </Tabs>
      <DeskPanel
        header={
          isPartnerTab ? (
            <div className="payment-setting-bank-toggle">
              <span>เปิด/ปิด ช่องทางธนาคาร</span>
              <Switch
                checked={isBankEnabled}
                checkedChildren="เปิด"
                unCheckedChildren="ปิด"
                disabled={!bankToggleTarget}
                loading={isUpdating}
                onChange={handleBankEnabledChange}
              />
            </div>
          ) : undefined
        }
        body={
          <Table
            columns={PaymentSettingColumns({
              paymentList: tabPaymentList,
              filteredInfo,
              handleUpdatePaymentSetting,
            })}
            dataSource={tabPaymentList}
            onChange={handleChange}
            loading={isLoading || isUpdating}
          />
        }
      />
    </div>
  );
};

export default PaymentSettingPage;
